import React, { useEffect, useState } from 'react';
import { TrackedProduct } from '../types';
import { getTrackedProducts, deleteTrackedProduct, updateTrackedProduct } from '../utils/api';
import { useTrackedStores } from '../hooks/useTrackedStores';

const REGION_FLAGS: Record<string, string> = {
  ES: '🇪🇸', NL: '🇳🇱', DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹',
  UK: '🇬🇧', GB: '🇬🇧', BE: '🇧🇪', US: '🇺🇸', EU: '🇪🇺', JP: '🇯🇵',
};

function Stars({ rating }: { rating: number }) {
  if (!rating) return <span style={{ color: '#8a93b0', fontSize: '11px' }}>No rating</span>;
  return (
    <span style={{ color: '#eab308', fontSize: '11px' }}>
      {'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}
      <span style={{ color: '#8a93b0', marginLeft: '4px' }}>{rating.toFixed(1)}</span>
    </span>
  );
}

export function TrackedProductsPanel() {
  const [products, setProducts] = useState<TrackedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editEmail, setEditEmail] = useState(false);
  const [editTelegram, setEditTelegram] = useState(false);
  const [msg, setMsg] = useState('');
  const { stores, untrackStore } = useTrackedStores();

  async function load() {
    try {
      const { trackedProducts } = await getTrackedProducts();
      setProducts(trackedProducts);
    } catch {
      setMsg('Failed to load tracked products.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startEdit(p: TrackedProduct) {
    setEditId(p.id);
    setEditPrice(p.targetPrice?.toString() ?? '');
    setEditEmail(p.notifyByEmail);
    setEditTelegram(p.notifyByTelegram);
  }

  async function saveEdit(id: string) {
    try {
      const parsed = editPrice ? parseFloat(editPrice) : undefined;
      await updateTrackedProduct(id, {
        targetPrice: parsed,
        notifyByEmail: editEmail,
        notifyByTelegram: editTelegram,
      });
      setEditId(null);
      setMsg('Updated successfully.');
      await load();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Update failed.');
    }
    setTimeout(() => setMsg(''), 3000);
  }

  async function handleRemove(id: string, title: string) {
    if (!confirm(`Remove tracking for "${title}"?`)) return;
    try {
      await deleteTrackedProduct(id);
      setMsg(`Removed "${title}".`);
      await load();
    } catch {
      setMsg('Failed to remove.');
    }
    setTimeout(() => setMsg(''), 3000);
  }

  function handleRemoveStore(id: string, name: string) {
    if (!confirm(`Remove saved store "${name}"?`)) return;
    untrackStore(id);
  }

  if (loading) return <div style={styles.empty}>Loading...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {msg && <div style={styles.toast}>{msg}</div>}

      {/* ── Tracked Products ─────────────────────────────────────────────── */}
      <section>
        <h3 style={styles.sectionTitle}>Tracked Products</h3>
        {products.length === 0 ? (
          <div style={styles.empty}>
            No tracked products yet. Search for a product and click "Track this product".
          </div>
        ) : (
          <div style={styles.list}>
            {products.map((p) => (
              <div key={p.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div>
                    <div style={styles.title}>{p.productTitle}</div>
                    <div style={styles.sub}>{p.productType} · Regions: {p.regions.join(', ')}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {editId === p.id ? (
                      <>
                        <button style={styles.saveBtn} onClick={() => saveEdit(p.id)}>Save</button>
                        <button style={styles.cancelBtn} onClick={() => setEditId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button style={styles.editBtn} onClick={() => startEdit(p)}>Edit</button>
                        <button style={styles.removeBtn} onClick={() => handleRemove(p.id, p.productTitle)}>Remove</button>
                      </>
                    )}
                  </div>
                </div>

                {editId === p.id ? (
                  <div style={styles.editForm}>
                    <label style={styles.lbl}>Target Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value)}
                      placeholder="No target"
                      style={styles.input}
                    />
                    <label style={styles.chkLbl}>
                      <input type="checkbox" checked={editEmail} onChange={(e) => setEditEmail(e.target.checked)} />
                      Email notifications
                    </label>
                    <label style={styles.chkLbl}>
                      <input type="checkbox" checked={editTelegram} onChange={(e) => setEditTelegram(e.target.checked)} />
                      Telegram notifications
                    </label>
                  </div>
                ) : (
                  <div style={styles.meta}>
                    <span>Target: {p.targetPrice !== undefined ? `${p.targetPrice.toFixed(2)}` : '—'}</span>
                    <span>📧 {p.notifyByEmail ? 'Email on' : 'Email off'}</span>
                    <span>💬 {p.notifyByTelegram ? 'Telegram on' : 'Telegram off'}</span>
                    <span style={{ color: '#8a93b0', fontSize: '12px' }}>
                      Added {new Date(p.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Saved Stores ─────────────────────────────────────────────────── */}
      <section>
        <h3 style={styles.sectionTitle}>
          Saved Stores
          <span style={styles.count}>{stores.length}</span>
        </h3>

        {stores.length === 0 ? (
          <div style={styles.empty}>
            No saved stores yet. Open any product's store list and click ☆ to save a store.
          </div>
        ) : (
          <div style={styles.storeGrid}>
            {stores.map((store) => (
              <div key={store.id} style={styles.storeCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{REGION_FLAGS[store.region] ?? REGION_FLAGS[store.country] ?? '🌐'}</span>
                      <span style={styles.storeName}>{store.name}</span>
                    </div>
                    <span style={styles.storeRegion}>{store.region}</span>
                    <Stars rating={store.rating} />
                  </div>
                  <button
                    style={styles.removeBtn}
                    onClick={() => handleRemoveStore(store.id, store.name)}
                  >
                    Remove
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {store.url && (
                    <a
                      href={store.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.storeLink}
                    >
                      Visit store →
                    </a>
                  )}
                  <span style={styles.savedAt}>
                    Saved {new Date(store.savedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  empty: { padding: '32px', textAlign: 'center', color: '#8a93b0', fontSize: '14px', background: '#1a1d27', borderRadius: '10px', border: '1.5px solid #2e3347' },
  toast: {
    padding: '12px 16px', background: '#3b82f620', border: '1px solid #3b82f6',
    borderRadius: '8px', color: '#3b82f6', fontSize: '14px',
  },
  sectionTitle: {
    fontSize: '15px', fontWeight: 700, color: '#e8eaf0', marginBottom: '14px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  count: {
    background: '#2e3347', color: '#8a93b0', fontSize: '12px',
    padding: '1px 8px', borderRadius: '10px', fontWeight: 600,
  },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    background: '#1a1d27', borderRadius: '10px', padding: '16px',
    border: '1.5px solid #2e3347',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' },
  title: { fontSize: '14px', fontWeight: 700, marginBottom: '3px' },
  sub: { fontSize: '12px', color: '#8a93b0' },
  meta: { display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#e8eaf0' },
  editForm: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' },
  lbl: { fontSize: '12px', fontWeight: 600, color: '#8a93b0', textTransform: 'uppercase' },
  input: {
    padding: '8px 12px', background: '#22263a', border: '1.5px solid #2e3347',
    borderRadius: '7px', color: '#e8eaf0', fontSize: '14px', outline: 'none',
  },
  chkLbl: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' },
  editBtn: {
    padding: '6px 14px', background: '#3b82f620', border: '1px solid #3b82f6',
    borderRadius: '6px', color: '#3b82f6', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
  },
  removeBtn: {
    padding: '6px 14px', background: '#ef444420', border: '1px solid #ef4444',
    borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
  },
  saveBtn: {
    padding: '6px 14px', background: '#22c55e', border: 'none',
    borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
  },
  cancelBtn: {
    padding: '6px 14px', background: 'transparent', border: '1px solid #2e3347',
    borderRadius: '6px', color: '#8a93b0', cursor: 'pointer', fontSize: '13px',
  },
  storeGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px',
  },
  storeCard: {
    background: '#1a1d27', border: '1.5px solid #eab30840', borderRadius: '10px',
    padding: '16px', display: 'flex', flexDirection: 'column',
  },
  storeName: { fontSize: '14px', fontWeight: 700, color: '#e8eaf0' },
  storeRegion: { fontSize: '12px', color: '#8a93b0' },
  storeLink: {
    fontSize: '12px', color: '#3b82f6', textDecoration: 'none',
    padding: '4px 10px', border: '1px solid #3b82f640', borderRadius: '5px', fontWeight: 600,
  },
  savedAt: { fontSize: '11px', color: '#8a93b0', alignSelf: 'center' },
};
